import {useEffect, useRef} from "react";

import {createRpLeadPerformanceDebugTrace} from "../../app/performance_debug";
import {authSucceeded} from "../../auth/actions/auth_succeeded_event";
import {emailSignUp} from "../../auth/api/email_sign_up";
import {getSmsLoginError, isSmsLoginAccountMismatchError, usePostSmsLoginInitMutation} from "../../auth/api/sms_login";
import {closeFlow} from "../../auth/state/registration_flow_slice";
import {AuthActionType} from "../../auth/types/AuthActionType";
import {notifyBugsnag} from "../../errors/bugsnag/notify_bugsnag";
import {addNotification} from "../../notifications/state/notifications_slice";
import {addFailureNotification} from "../../notifications/utils/add_failure_notification";
import {defaultAPIErrorNotification} from "../../notifications/utils/error_notifications";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import {gtmEventApplicationRegister} from "../../tracking/google_tag_manager/gtm_event_application_register";
import {UserSource} from "../../tracking/tracking_utils/user_source";
import {getIsUserAuthenticatedApi} from "../../user/api/get_is_user_authenticated";
import {getUserProfileApi, useGetUserProfileQuery} from "../../user/api/get_user_profile";
import {usePatchUserProfileMutation} from "../../user/api/patch_user_profile";
import {useIsAuthenticated} from "../../user/hooks/use_is_authenticated";
import {IUserProfile} from "../../user/types/IUserProfile";
import {UserAccountType} from "../../user/types/UserAccountType";
import {getUserFirstName} from "../../user/utils/get_user_first_name";
import {isValidationError} from "../../utils/isValidationError";
import {ApplicationModalStateEvent, IApplicationModalStateContext} from "../reducers/application_modal_state_transitions";
import {IApplicationModalState, IApplicationModalStateData} from "../reducers/application_modal_step_reducer";
import {setContinuePromotionFlowAfterAuth, setPromotionLeadDraft} from "../state/main_lead_slice";
import {ApplicationModalStep} from "../types/ApplicationModalStep";
import {IApplicationFormValues} from "../types/form/IApplicationFormValues";
import {IApplicationData} from "../types/IApplicationData";
import {IAmplitudeLeadFormSubmitContext} from "../types/lead/IAmplitudeLeadFormEventTracking";
import {IPromotionLeadDraft} from "../types/lead/IPromotionLeadDraft";
import {useSendMainLead} from "./use_send_main_lead";

interface IProps {
    handleModalClose: () => void;
    modalState: IApplicationModalState;
    onStandardLeadSuccess?: (sentApplication: IApplicationData | null) => Promise<void>;
    sendModalEvent: (
        event: ApplicationModalStateEvent,
        dataUpdates?: Partial<IApplicationModalStateData>,
        contextUpdates?: Partial<IApplicationModalStateContext>
    ) => void;
}

export const usePromotionLeadFlow = (props: IProps) => {
    const {handleModalClose, modalState, onStandardLeadSuccess, sendModalEvent} = props;
    const dispatch = useAppDispatch();
    const sendLead = useSendMainLead();
    const [postSmsLoginInit] = usePostSmsLoginInitMutation();

    const mainLead = useAppSelector((state) => state.mainLead);
    const registrationFlow = useAppSelector((state) => state.registrationFlow);
    const isAuthenticated = useIsAuthenticated();
    const {data: userProfile} = useGetUserProfileQuery();
    const [patchUserProfile] = usePatchUserProfileMutation();

    const authFlowWasOpenRef = useRef(false);
    const startedAuthenticatedRef = useRef(false);
    const authActionDuringPromotionFlowRef = useRef<AuthActionType | null>(null);
    const registerEventSentRef = useRef(false);
    const draftSourceFormStepRef = useRef<ApplicationModalStep>(ApplicationModalStep.PROMOTION_FORM);

    const isPromotionFlow = mainLead.flowVariant === "promotion";
    const getDraftPhone = (draft: IPromotionLeadDraft) => `${draft.phone.code} ${draft.phone.number}`;
    const getDraftSmsLoginPhone = (draft: IPromotionLeadDraft) => `+${draft.phone.code}${draft.phone.number.replace(/\D/g, "")}`;
    const normalizePhone = (phone: string | null | undefined) => phone?.replace(/\D/g, "") || "";
    const isDraftPhoneVerifiedForProfile = (draft: IPromotionLeadDraft, profile: IUserProfile | null | undefined) =>
        Boolean(profile?.phone_verified && normalizePhone(profile.phone) === normalizePhone(getDraftPhone(draft)));
    const getSmsLoginMismatchMessage = (name: string) => {
        const firstName = getUserFirstName(name);
        const messageWithoutName =
            "Masz już u nas konto, ale wpisany numer telefonu jest inny niż podawany wcześniej. Popraw numer, aby kontynuować, lub podaj inny adres e-mail, by założyć nowe konto.";
        const messageWithName =
            "masz już u nas konto, ale wpisany numer telefonu jest inny niż podawany wcześniej. Popraw numer, aby kontynuować, lub podaj inny adres e-mail, by założyć nowe konto.";

        return firstName ? `${firstName}, ${messageWithName}` : messageWithoutName;
    };
    const isForbiddenRegistrationError = (error: unknown) => {
        if (typeof error !== "object" || error === null) {
            return false;
        }

        if ("response" in error && (error as {response?: {status?: number}}).response?.status === 403) {
            return true;
        }

        return "status" in error && (error as {status?: unknown}).status === 403;
    };

    const syncDraftToUserProfile = async (draft: IPromotionLeadDraft, profile = userProfile, shouldSkipSync = false) => {
        if (shouldSkipSync) {
            return profile;
        }

        const draftPhone = getDraftPhone(draft);
        const shouldPatchProfile = profile?.name !== draft.name || normalizePhone(profile?.phone) !== normalizePhone(draftPhone);

        if (!shouldPatchProfile) {
            return profile;
        }

        await patchUserProfile({
            name: draft.name,
            phone: draftPhone
        }).unwrap();

        return await dispatch(getUserProfileApi.endpoints.getUserProfile.initiate(undefined, {forceRefetch: true})).unwrap();
    };

    const sendDraftLead = async (draft: IPromotionLeadDraft) => {
        const trace = createRpLeadPerformanceDebugTrace("rp_promotion_send_draft_lead", {
            isPromotionFlow,
            offerId: draft.offerId,
            propertyId: draft.propertyId,
            source: draft.source,
            sourceSection: draft.sourceSection,
            vendorId: draft.vendorId
        });
        const {
            vendorId: _vendorId,
            offerId: _offerId,
            propertyId: _propertyId,
            source: _source,
            sourceSection: _sourceSection,
            amplitudeSubmitContext,
            ...leadFormValues
        } = draft;

        try {
            trace.mark("send_lead:start");
            const response = await sendLead(leadFormValues, () => handleModalClose(), {
                ...(amplitudeSubmitContext ? {analyticsParams: {amplitudeLeadFormSubmitContext: amplitudeSubmitContext}} : {}),
                ...(isPromotionFlow ? {persistFormValues: false} : {})
            });
            trace.end("send_lead:done", {
                hasResponse: Boolean(response),
                userCreated: Boolean(response?.user_created)
            });

            return response;
        } catch (error) {
            trace.end("send_lead:error");
            throw error;
        }
    };

    const finalizeStandardLead = async (draft: IPromotionLeadDraft) => {
        const trace = createRpLeadPerformanceDebugTrace("rp_promotion_finalize_standard_lead", {
            isPromotionFlow,
            offerId: draft.offerId,
            propertyId: draft.propertyId,
            source: draft.source,
            sourceSection: draft.sourceSection,
            vendorId: draft.vendorId
        });
        trace.mark("send_draft_lead:start");
        const application = await sendDraftLead(draft);
        trace.mark("send_draft_lead:done", {hasApplication: Boolean(application)});

        if (onStandardLeadSuccess) {
            trace.mark("on_standard_lead_success:start");
            await onStandardLeadSuccess(application || null);
            trace.end("on_standard_lead_success:done");
            return;
        }

        handleModalClose();
        trace.end("modal_close:done");
    };

    const finalizePromotionLead = async (draft: IPromotionLeadDraft) => {
        const trace = createRpLeadPerformanceDebugTrace("rp_promotion_finalize_promotion_lead", {
            isPromotionFlow,
            offerId: draft.offerId,
            propertyId: draft.propertyId,
            source: draft.source,
            sourceSection: draft.sourceSection,
            vendorId: draft.vendorId
        });
        sendModalEvent({type: "PROMOTION_SUBMIT_STARTED"});
        trace.mark("modal_event:submit_started");

        try {
            trace.mark("send_draft_lead:start");
            const application = await sendDraftLead(draft);
            trace.mark("send_draft_lead:done", {hasApplication: Boolean(application)});

            if (
                application &&
                !application.user_created &&
                !startedAuthenticatedRef.current &&
                authActionDuringPromotionFlowRef.current === AuthActionType.REGISTRATION &&
                !registerEventSentRef.current
            ) {
                registerEventSentRef.current = true;
                gtmEventApplicationRegister();
                trace.mark("gtm_application_register:done");
            }

            const shouldShowPromotionSuccessModal = !startedAuthenticatedRef.current && authActionDuringPromotionFlowRef.current !== AuthActionType.LOGIN;

            if (!shouldShowPromotionSuccessModal) {
                handleModalClose();
                trace.end("modal_close:done");
                return;
            }

            sendModalEvent({type: "PROMOTION_SUBMIT_SUCCEEDED"}, {originalApplicationResponse: application || null});
            trace.end("modal_event:submit_succeeded");
        } catch {
            dispatch(addFailureNotification("Nie udało się wysłać zgłoszenia."));
            sendModalEvent({type: "PROMOTION_SUBMIT_FAILED"});
            trace.end("modal_event:submit_failed");
        }
    };

    const finalizeLeadAfterAuthentication = async (draft: IPromotionLeadDraft) => {
        if (isPromotionFlow) {
            await finalizePromotionLead(draft);
            return;
        }

        await finalizeStandardLead(draft);
    };

    const getSourceFormStep = () => (isPromotionFlow ? undefined : draftSourceFormStepRef.current);
    const getEditPhoneEvent = (): ApplicationModalStateEvent => {
        const step = getSourceFormStep();

        return step !== undefined ? {type: "PROMOTION_EDIT_PHONE", step} : {type: "PROMOTION_EDIT_PHONE"};
    };
    const sendSubmitFailedEvent = () => {
        const step = getSourceFormStep();
        sendModalEvent(step !== undefined ? {type: "PROMOTION_SUBMIT_FAILED", step} : {type: "PROMOTION_SUBMIT_FAILED"});
    };

    const continueLeadAfterAuthentication = async (profile: IUserProfile) => {
        const draft = mainLead.promotionLeadDraft;

        if (!draft) {
            handleModalClose();
            return;
        }

        try {
            if (isDraftPhoneVerifiedForProfile(draft, profile)) {
                await finalizeLeadAfterAuthentication(draft);
                return;
            }

            await syncDraftToUserProfile(draft, profile, false);
            sendModalEvent({type: "PROMOTION_PHONE_VERIFICATION_REQUIRED"});
        } catch (_error) {
            dispatch(addNotification({notification: defaultAPIErrorNotification}));
            sendSubmitFailedEvent();
        }
    };

    const handleDraftSubmit = async (values: IApplicationFormValues, amplitudeSubmitContext?: IAmplitudeLeadFormSubmitContext) => {
        const trace = createRpLeadPerformanceDebugTrace("rp_promotion_draft_submit", {
            isAuthenticated,
            isPromotionFlow,
            modalStep: modalState.step
        });
        startedAuthenticatedRef.current = isAuthenticated;
        authActionDuringPromotionFlowRef.current = null;
        registerEventSentRef.current = false;
        draftSourceFormStepRef.current = modalState.step;
        trace.mark("draft_submit:start");

        const draft: IPromotionLeadDraft = {
            ...values,
            vendorId: mainLead.vendorId || 0,
            offerId: mainLead.offerId,
            propertyId: mainLead.propertyId,
            source: mainLead.source!,
            sourceSection: mainLead.sourceSection!,
            ...(amplitudeSubmitContext ? {amplitudeSubmitContext} : {})
        };

        dispatch(setPromotionLeadDraft(draft));
        trace.mark("draft:persisted", {
            offerId: draft.offerId,
            propertyId: draft.propertyId,
            source: draft.source,
            sourceSection: draft.sourceSection,
            vendorId: draft.vendorId
        });

        if (isAuthenticated && userProfile) {
            try {
                if (isDraftPhoneVerifiedForProfile(draft, userProfile)) {
                    trace.mark("authenticated_profile:phone_already_verified");
                    await finalizeLeadAfterAuthentication(draft);
                    trace.end("finalize_after_auth:done");
                    return;
                }

                trace.mark("profile_sync:start");
                await syncDraftToUserProfile(draft, userProfile);
                trace.mark("profile_sync:done");
                sendModalEvent({type: "PROMOTION_PHONE_VERIFICATION_REQUIRED"});
                trace.end("modal_event:phone_verification_required");
            } catch (_error) {
                dispatch(addNotification({notification: defaultAPIErrorNotification}));
                trace.end("authenticated_profile:error");
            }

            return;
        }

        if (isAuthenticated) {
            try {
                trace.mark("authenticated_without_profile:profile_sync_start");
                await syncDraftToUserProfile(draft, userProfile);
                trace.mark("authenticated_without_profile:profile_sync_done");
                sendModalEvent({type: "PROMOTION_PHONE_VERIFICATION_REQUIRED"});
                trace.end("modal_event:phone_verification_required");
            } catch (_error) {
                dispatch(addNotification({notification: defaultAPIErrorNotification}));
                trace.end("authenticated_without_profile:error");
            }

            return;
        }

        try {
            trace.mark("email_sign_up:start");
            await emailSignUp({
                email: draft.email,
                name: draft.name,
                phone: getDraftPhone(draft),
                type: UserAccountType.LOCAL,
                source: UserSource.REGISTRATION,
                marketing_email: draft.marketing_email,
                marketing_sms: draft.marketing_sms
            });
            trace.mark("email_sign_up:done");

            dispatch(
                authSucceeded({
                    type: AuthActionType.REGISTRATION,
                    userSource: UserSource.REGISTRATION,
                    registrationMethod: "email"
                })
            );
            authActionDuringPromotionFlowRef.current = AuthActionType.REGISTRATION;
            dispatch(setContinuePromotionFlowAfterAuth(true));
            trace.end("auth_succeeded:done");
        } catch (registrationError) {
            trace.mark("email_sign_up:error", {
                isValidationError: isValidationError(registrationError)
            });

            if (isValidationError(registrationError) && registrationError.appError.fieldErrors.phone?.length) {
                sendModalEvent(getEditPhoneEvent(), {
                    smsLoginErrorMessage: null,
                    smsLoginInitialSendResult: null
                });
                trace.end("registration_phone_validation:error");
                throw registrationError;
            }

            if (isForbiddenRegistrationError(registrationError)) {
                sendModalEvent(getEditPhoneEvent(), {
                    smsLoginErrorMessage: null,
                    smsLoginInitialSendResult: null
                });
                dispatch(addNotification({notification: defaultAPIErrorNotification}));
                trace.end("registration_forbidden:error");
                return;
            }

            let response;

            try {
                trace.mark("sms_login_init:start");
                response = await postSmsLoginInit({
                    email: draft.email,
                    phone: getDraftSmsLoginPhone(draft)
                });
                trace.mark("sms_login_init:done", {hasError: Boolean(response.error)});
            } catch {
                sendModalEvent(getEditPhoneEvent(), {
                    smsLoginErrorMessage: null,
                    smsLoginInitialSendResult: null
                });
                dispatch(addNotification({notification: defaultAPIErrorNotification}));
                trace.end("sms_login_init:error");
                return;
            }

            if (!response.error) {
                sendModalEvent(
                    {type: "PROMOTION_SMS_LOGIN_REQUIRED"},
                    {
                        smsLoginErrorMessage: null,
                        smsLoginInitialSendResult: {}
                    }
                );
                trace.end("modal_event:sms_login_required");
                return;
            }

            const error = getSmsLoginError(response.error, "Nie udało się wysłać kodu SMS.");

            if (isSmsLoginAccountMismatchError(error)) {
                sendModalEvent(getEditPhoneEvent(), {
                    smsLoginErrorMessage: getSmsLoginMismatchMessage(draft.name),
                    smsLoginInitialSendResult: null
                });
                trace.end("sms_login:account_mismatch");
                return;
            }

            sendModalEvent(getEditPhoneEvent(), {
                smsLoginErrorMessage: null,
                smsLoginInitialSendResult: null
            });
            dispatch(addNotification({notification: defaultAPIErrorNotification}));
            trace.end("sms_login:error");
        }
    };

    const handleSmsLoginRejected = (_error: {message: string; status: number}) => {
        sendModalEvent(getEditPhoneEvent(), {
            smsLoginErrorMessage: getSmsLoginMismatchMessage(mainLead.promotionLeadDraft?.name || ""),
            smsLoginInitialSendResult: null
        });
    };

    const handleSmsLoginSuccess = async () => {
        const draft = mainLead.promotionLeadDraft;

        if (!draft) {
            handleModalClose();
            return;
        }

        authActionDuringPromotionFlowRef.current = AuthActionType.LOGIN;

        dispatch(
            authSucceeded({
                type: AuthActionType.LOGIN,
                userSource: UserSource.REGISTRATION,
                registrationMethod: "sms"
            })
        );

        try {
            const isUserAuthenticated = await dispatch(
                getIsUserAuthenticatedApi.endpoints.getIsUserAuthenticated.initiate(undefined, {forceRefetch: true})
            ).unwrap();

            if (!isUserAuthenticated) {
                throw new Error("SMS login did not create an authenticated session");
            }

            const profile = await dispatch(getUserProfileApi.endpoints.getUserProfile.initiate(undefined, {forceRefetch: true})).unwrap();
            if (!profile) {
                throw new Error("SMS login did not return a user profile");
            }

            await syncDraftToUserProfile(draft, profile);
            await finalizeLeadAfterAuthentication(draft);
        } catch (error) {
            notifyBugsnag(error, "Lead SMS login post-authentication failed", {
                stage: "sms_login_post_auth",
                vendorId: draft.vendorId,
                offerId: draft.offerId,
                propertyId: draft.propertyId
            });
            dispatch(addNotification({notification: defaultAPIErrorNotification}));
            sendSubmitFailedEvent();
        }
    };

    const handlePhoneVerificationSuccess = async () => {
        const draft = mainLead.promotionLeadDraft;

        if (!draft) {
            handleModalClose();
            return;
        }

        await finalizeLeadAfterAuthentication(draft);
    };

    const handlePhoneVerificationChangeNumber = () => {
        sendModalEvent(getEditPhoneEvent(), {
            smsLoginErrorMessage: null,
            smsLoginInitialSendResult: null
        });
    };

    useEffect(() => {
        if (!mainLead.continuePromotionFlowAfterAuth) {
            authFlowWasOpenRef.current = false;
            return;
        }

        if (registrationFlow.isActive) {
            authFlowWasOpenRef.current = true;
            return;
        }

        if (!authFlowWasOpenRef.current || modalState.step !== ApplicationModalStep.PROMOTION_AUTH_FALLBACK || registrationFlow.lastAuthAction) {
            return;
        }

        authFlowWasOpenRef.current = false;
        dispatch(setContinuePromotionFlowAfterAuth(false));
        handleModalClose();
    }, [dispatch, handleModalClose, mainLead.continuePromotionFlowAfterAuth, modalState.step, registrationFlow.isActive, registrationFlow.lastAuthAction]);

    useEffect(() => {
        if (!mainLead.continuePromotionFlowAfterAuth || !isAuthenticated || !userProfile) {
            return;
        }

        if (modalState.step === ApplicationModalStep.PROMOTION_AUTH_FALLBACK && !registrationFlow.lastAuthAction) {
            return;
        }

        authFlowWasOpenRef.current = false;
        if (registrationFlow.lastAuthAction) {
            authActionDuringPromotionFlowRef.current = registrationFlow.lastAuthAction;
        }
        dispatch(setContinuePromotionFlowAfterAuth(false));
        if (registrationFlow.isActive) {
            dispatch(closeFlow());
        }
        void continueLeadAfterAuthentication(userProfile);
    }, [
        dispatch,
        isAuthenticated,
        mainLead.continuePromotionFlowAfterAuth,
        modalState.step,
        registrationFlow.isActive,
        registrationFlow.lastAuthAction,
        userProfile
    ]);

    return {
        handleDraftSubmit,
        handlePhoneVerificationSuccess,
        handlePhoneVerificationChangeNumber,
        handleSmsLoginRejected,
        handleSmsLoginSuccess
    };
};
import {useEffect, useRef} from "react";

import {createRpLeadPerformanceDebugTrace} from "../../app/performance_debug";
import {authSucceeded} from "../../auth/actions/auth_succeeded_event";
import {emailSignUp} from "../../auth/api/email_sign_up";
import {getSmsLoginError, isSmsLoginAccountMismatchError, usePostSmsLoginInitMutation} from "../../auth/api/sms_login";
import {closeFlow} from "../../auth/state/registration_flow_slice";
import {AuthActionType} from "../../auth/types/AuthActionType";
import {notifyBugsnag} from "../../errors/bugsnag/notify_bugsnag";
import {addNotification} from "../../notifications/state/notifications_slice";
import {addFailureNotification} from "../../notifications/utils/add_failure_notification";
import {defaultAPIErrorNotification} from "../../notifications/utils/error_notifications";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import {gtmEventApplicationRegister} from "../../tracking/google_tag_manager/gtm_event_application_register";
import {UserSource} from "../../tracking/tracking_utils/user_source";
import {getIsUserAuthenticatedApi} from "../../user/api/get_is_user_authenticated";
import {getUserProfileApi, useGetUserProfileQuery} from "../../user/api/get_user_profile";
import {usePatchUserProfileMutation} from "../../user/api/patch_user_profile";
import {useIsAuthenticated} from "../../user/hooks/use_is_authenticated";
import {IUserProfile} from "../../user/types/IUserProfile";
import {UserAccountType} from "../../user/types/UserAccountType";
import {getUserFirstName} from "../../user/utils/get_user_first_name";
import {isValidationError} from "../../utils/isValidationError";
import {ApplicationModalStateEvent, IApplicationModalStateContext} from "../reducers/application_modal_state_transitions";
import {IApplicationModalState, IApplicationModalStateData} from "../reducers/application_modal_step_reducer";
import {setContinuePromotionFlowAfterAuth, setPromotionLeadDraft} from "../state/main_lead_slice";
import {ApplicationModalStep} from "../types/ApplicationModalStep";
import {IApplicationFormValues} from "../types/form/IApplicationFormValues";
import {IApplicationData} from "../types/IApplicationData";
import {IAmplitudeLeadFormSubmitContext} from "../types/lead/IAmplitudeLeadFormEventTracking";
import {IPromotionLeadDraft} from "../types/lead/IPromotionLeadDraft";
import {useSendMainLead} from "./use_send_main_lead";

interface IProps {
    handleModalClose: () => void;
    modalState: IApplicationModalState;
    onStandardLeadSuccess?: (sentApplication: IApplicationData | null) => Promise<void>;
    sendModalEvent: (
        event: ApplicationModalStateEvent,
        dataUpdates?: Partial<IApplicationModalStateData>,
        contextUpdates?: Partial<IApplicationModalStateContext>
    ) => void;
}

export const usePromotionLeadFlow = (props: IProps) => {
    const {handleModalClose, modalState, onStandardLeadSuccess, sendModalEvent} = props;
    const dispatch = useAppDispatch();
    const sendLead = useSendMainLead();
    const [postSmsLoginInit] = usePostSmsLoginInitMutation();

    const mainLead = useAppSelector((state) => state.mainLead);
    const registrationFlow = useAppSelector((state) => state.registrationFlow);
    const isAuthenticated = useIsAuthenticated();
    const {data: userProfile} = useGetUserProfileQuery();
    const [patchUserProfile] = usePatchUserProfileMutation();

    const authFlowWasOpenRef = useRef(false);
    const startedAuthenticatedRef = useRef(false);
    const authActionDuringPromotionFlowRef = useRef<AuthActionType | null>(null);
    const registerEventSentRef = useRef(false);
    const draftSourceFormStepRef = useRef<ApplicationModalStep>(ApplicationModalStep.PROMOTION_FORM);

    const isPromotionFlow = mainLead.flowVariant === "promotion";
    const getDraftPhone = (draft: IPromotionLeadDraft) => `${draft.phone.code} ${draft.phone.number}`;
    const getDraftSmsLoginPhone = (draft: IPromotionLeadDraft) => `+${draft.phone.code}${draft.phone.number.replace(/\D/g, "")}`;
    const normalizePhone = (phone: string | null | undefined) => phone?.replace(/\D/g, "") || "";
    const isDraftPhoneVerifiedForProfile = (draft: IPromotionLeadDraft, profile: IUserProfile | null | undefined) =>
        Boolean(profile?.phone_verified && normalizePhone(profile.phone) === normalizePhone(getDraftPhone(draft)));
    const getSmsLoginMismatchMessage = (name: string) => {
        const firstName = getUserFirstName(name);
        const messageWithoutName =
            "Masz już u nas konto, ale wpisany numer telefonu jest inny niż podawany wcześniej. Popraw numer, aby kontynuować, lub podaj inny adres e-mail, by założyć nowe konto.";
        const messageWithName =
            "masz już u nas konto, ale wpisany numer telefonu jest inny niż podawany wcześniej. Popraw numer, aby kontynuować, lub podaj inny adres e-mail, by założyć nowe konto.";

        return firstName ? `${firstName}, ${messageWithName}` : messageWithoutName;
    };
    const isForbiddenRegistrationError = (error: unknown) => {
        if (typeof error !== "object" || error === null) {
            return false;
        }

        if ("response" in error && (error as {response?: {status?: number}}).response?.status === 403) {
            return true;
        }

        return "status" in error && (error as {status?: unknown}).status === 403;
    };

    const syncDraftToUserProfile = async (draft: IPromotionLeadDraft, profile = userProfile, shouldSkipSync = false) => {
        if (shouldSkipSync) {
            return profile;
        }

        const draftPhone = getDraftPhone(draft);
        const shouldPatchProfile = profile?.name !== draft.name || normalizePhone(profile?.phone) !== normalizePhone(draftPhone);

        if (!shouldPatchProfile) {
            return profile;
        }

        await patchUserProfile({
            name: draft.name,
            phone: draftPhone
        }).unwrap();

        return await dispatch(getUserProfileApi.endpoints.getUserProfile.initiate(undefined, {forceRefetch: true})).unwrap();
    };

    const sendDraftLead = async (draft: IPromotionLeadDraft) => {
        const trace = createRpLeadPerformanceDebugTrace("rp_promotion_send_draft_lead", {
            isPromotionFlow,
            offerId: draft.offerId,
            propertyId: draft.propertyId,
            source: draft.source,
            sourceSection: draft.sourceSection,
            vendorId: draft.vendorId
        });
        const {
            vendorId: _vendorId,
            offerId: _offerId,
            propertyId: _propertyId,
            source: _source,
            sourceSection: _sourceSection,
            amplitudeSubmitContext,
            ...leadFormValues
        } = draft;

        try {
            trace.mark("send_lead:start");
            const response = await sendLead(leadFormValues, () => handleModalClose(), {
                ...(amplitudeSubmitContext ? {analyticsParams: {amplitudeLeadFormSubmitContext: amplitudeSubmitContext}} : {}),
                ...(isPromotionFlow ? {persistFormValues: false} : {})
            });
            trace.end("send_lead:done", {
                hasResponse: Boolean(response),
                userCreated: Boolean(response?.user_created)
            });

            return response;
        } catch (error) {
            trace.end("send_lead:error");
            throw error;
        }
    };

    const finalizeStandardLead = async (draft: IPromotionLeadDraft) => {
        const trace = createRpLeadPerformanceDebugTrace("rp_promotion_finalize_standard_lead", {
            isPromotionFlow,
            offerId: draft.offerId,
            propertyId: draft.propertyId,
            source: draft.source,
            sourceSection: draft.sourceSection,
            vendorId: draft.vendorId
        });
        trace.mark("send_draft_lead:start");
        const application = await sendDraftLead(draft);
        trace.mark("send_draft_lead:done", {hasApplication: Boolean(application)});

        if (onStandardLeadSuccess) {
            trace.mark("on_standard_lead_success:start");
            await onStandardLeadSuccess(application || null);
            trace.end("on_standard_lead_success:done");
            return;
        }

        handleModalClose();
        trace.end("modal_close:done");
    };

    const finalizePromotionLead = async (draft: IPromotionLeadDraft) => {
        const trace = createRpLeadPerformanceDebugTrace("rp_promotion_finalize_promotion_lead", {
            isPromotionFlow,
            offerId: draft.offerId,
            propertyId: draft.propertyId,
            source: draft.source,
            sourceSection: draft.sourceSection,
            vendorId: draft.vendorId
        });
        sendModalEvent({type: "PROMOTION_SUBMIT_STARTED"});
        trace.mark("modal_event:submit_started");

        try {
            trace.mark("send_draft_lead:start");
            const application = await sendDraftLead(draft);
            trace.mark("send_draft_lead:done", {hasApplication: Boolean(application)});

            if (
                application &&
                !application.user_created &&
                !startedAuthenticatedRef.current &&
                authActionDuringPromotionFlowRef.current === AuthActionType.REGISTRATION &&
                !registerEventSentRef.current
            ) {
                registerEventSentRef.current = true;
                gtmEventApplicationRegister();
                trace.mark("gtm_application_register:done");
            }

            const shouldShowPromotionSuccessModal = !startedAuthenticatedRef.current && authActionDuringPromotionFlowRef.current !== AuthActionType.LOGIN;

            if (!shouldShowPromotionSuccessModal) {
                handleModalClose();
                trace.end("modal_close:done");
                return;
            }

            sendModalEvent({type: "PROMOTION_SUBMIT_SUCCEEDED"}, {originalApplicationResponse: application || null});
            trace.end("modal_event:submit_succeeded");
        } catch {
            dispatch(addFailureNotification("Nie udało się wysłać zgłoszenia."));
            sendModalEvent({type: "PROMOTION_SUBMIT_FAILED"});
            trace.end("modal_event:submit_failed");
        }
    };

    const finalizeLeadAfterAuthentication = async (draft: IPromotionLeadDraft) => {
        if (isPromotionFlow) {
            await finalizePromotionLead(draft);
            return;
        }

        await finalizeStandardLead(draft);
    };

    const getSourceFormStep = () => (isPromotionFlow ? undefined : draftSourceFormStepRef.current);
    const getEditPhoneEvent = (): ApplicationModalStateEvent => {
        const step = getSourceFormStep();

        return step !== undefined ? {type: "PROMOTION_EDIT_PHONE", step} : {type: "PROMOTION_EDIT_PHONE"};
    };
    const sendSubmitFailedEvent = () => {
        const step = getSourceFormStep();
        sendModalEvent(step !== undefined ? {type: "PROMOTION_SUBMIT_FAILED", step} : {type: "PROMOTION_SUBMIT_FAILED"});
    };

    const continueLeadAfterAuthentication = async (profile: IUserProfile) => {
        const draft = mainLead.promotionLeadDraft;

        if (!draft) {
            handleModalClose();
            return;
        }

        try {
            if (isDraftPhoneVerifiedForProfile(draft, profile)) {
                await finalizeLeadAfterAuthentication(draft);
                return;
            }

            await syncDraftToUserProfile(draft, profile, false);
            sendModalEvent({type: "PROMOTION_PHONE_VERIFICATION_REQUIRED"});
        } catch (_error) {
            dispatch(addNotification({notification: defaultAPIErrorNotification}));
            sendSubmitFailedEvent();
        }
    };

    const handleDraftSubmit = async (values: IApplicationFormValues, amplitudeSubmitContext?: IAmplitudeLeadFormSubmitContext) => {
        const trace = createRpLeadPerformanceDebugTrace("rp_promotion_draft_submit", {
            isAuthenticated,
            isPromotionFlow,
            modalStep: modalState.step
        });
        startedAuthenticatedRef.current = isAuthenticated;
        authActionDuringPromotionFlowRef.current = null;
        registerEventSentRef.current = false;
        draftSourceFormStepRef.current = modalState.step;
        trace.mark("draft_submit:start");

        const draft: IPromotionLeadDraft = {
            ...values,
            vendorId: mainLead.vendorId || 0,
            offerId: mainLead.offerId,
            propertyId: mainLead.propertyId,
            source: mainLead.source!,
            sourceSection: mainLead.sourceSection!,
            ...(amplitudeSubmitContext ? {amplitudeSubmitContext} : {})
        };

        dispatch(setPromotionLeadDraft(draft));
        trace.mark("draft:persisted", {
            offerId: draft.offerId,
            propertyId: draft.propertyId,
            source: draft.source,
            sourceSection: draft.sourceSection,
            vendorId: draft.vendorId
        });

        if (isAuthenticated && userProfile) {
            try {
                if (isDraftPhoneVerifiedForProfile(draft, userProfile)) {
                    trace.mark("authenticated_profile:phone_already_verified");
                    await finalizeLeadAfterAuthentication(draft);
                    trace.end("finalize_after_auth:done");
                    return;
                }

                trace.mark("profile_sync:start");
                await syncDraftToUserProfile(draft, userProfile);
                trace.mark("profile_sync:done");
                sendModalEvent({type: "PROMOTION_PHONE_VERIFICATION_REQUIRED"});
                trace.end("modal_event:phone_verification_required");
            } catch (_error) {
                dispatch(addNotification({notification: defaultAPIErrorNotification}));
                trace.end("authenticated_profile:error");
            }

            return;
        }

        if (isAuthenticated) {
            try {
                trace.mark("authenticated_without_profile:profile_sync_start");
                await syncDraftToUserProfile(draft, userProfile);
                trace.mark("authenticated_without_profile:profile_sync_done");
                sendModalEvent({type: "PROMOTION_PHONE_VERIFICATION_REQUIRED"});
                trace.end("modal_event:phone_verification_required");
            } catch (_error) {
                dispatch(addNotification({notification: defaultAPIErrorNotification}));
                trace.end("authenticated_without_profile:error");
            }

            return;
        }

        try {
            trace.mark("email_sign_up:start");
            await emailSignUp({
                email: draft.email,
                name: draft.name,
                phone: getDraftPhone(draft),
                type: UserAccountType.LOCAL,
                source: UserSource.REGISTRATION,
                marketing_email: draft.marketing_email,
                marketing_sms: draft.marketing_sms
            });
            trace.mark("email_sign_up:done");

            dispatch(
                authSucceeded({
                    type: AuthActionType.REGISTRATION,
                    userSource: UserSource.REGISTRATION,
                    registrationMethod: "email"
                })
            );
            authActionDuringPromotionFlowRef.current = AuthActionType.REGISTRATION;
            dispatch(setContinuePromotionFlowAfterAuth(true));
            trace.end("auth_succeeded:done");
        } catch (registrationError) {
            trace.mark("email_sign_up:error", {
                isValidationError: isValidationError(registrationError)
            });

            if (isValidationError(registrationError) && registrationError.appError.fieldErrors.phone?.length) {
                sendModalEvent(getEditPhoneEvent(), {
                    smsLoginErrorMessage: null,
                    smsLoginInitialSendResult: null
                });
                trace.end("registration_phone_validation:error");
                throw registrationError;
            }

            if (isForbiddenRegistrationError(registrationError)) {
                sendModalEvent(getEditPhoneEvent(), {
                    smsLoginErrorMessage: null,
                    smsLoginInitialSendResult: null
                });
                dispatch(addNotification({notification: defaultAPIErrorNotification}));
                trace.end("registration_forbidden:error");
                return;
            }

            let response;

            try {
                trace.mark("sms_login_init:start");
                response = await postSmsLoginInit({
                    email: draft.email,
                    phone: getDraftSmsLoginPhone(draft)
                });
                trace.mark("sms_login_init:done", {hasError: Boolean(response.error)});
            } catch {
                sendModalEvent(getEditPhoneEvent(), {
                    smsLoginErrorMessage: null,
                    smsLoginInitialSendResult: null
                });
                dispatch(addNotification({notification: defaultAPIErrorNotification}));
                trace.end("sms_login_init:error");
                return;
            }

            if (!response.error) {
                sendModalEvent(
                    {type: "PROMOTION_SMS_LOGIN_REQUIRED"},
                    {
                        smsLoginErrorMessage: null,
                        smsLoginInitialSendResult: {}
                    }
                );
                trace.end("modal_event:sms_login_required");
                return;
            }

            const error = getSmsLoginError(response.error, "Nie udało się wysłać kodu SMS.");

            if (isSmsLoginAccountMismatchError(error)) {
                sendModalEvent(getEditPhoneEvent(), {
                    smsLoginErrorMessage: getSmsLoginMismatchMessage(draft.name),
                    smsLoginInitialSendResult: null
                });
                trace.end("sms_login:account_mismatch");
                return;
            }

            sendModalEvent(getEditPhoneEvent(), {
                smsLoginErrorMessage: null,
                smsLoginInitialSendResult: null
            });
            dispatch(addNotification({notification: defaultAPIErrorNotification}));
            trace.end("sms_login:error");
        }
    };

    const handleSmsLoginRejected = (_error: {message: string; status: number}) => {
        sendModalEvent(getEditPhoneEvent(), {
            smsLoginErrorMessage: getSmsLoginMismatchMessage(mainLead.promotionLeadDraft?.name || ""),
            smsLoginInitialSendResult: null
        });
    };

    const handleSmsLoginSuccess = async () => {
        const draft = mainLead.promotionLeadDraft;

        if (!draft) {
            handleModalClose();
            return;
        }

        authActionDuringPromotionFlowRef.current = AuthActionType.LOGIN;

        dispatch(
            authSucceeded({
                type: AuthActionType.LOGIN,
                userSource: UserSource.REGISTRATION,
                registrationMethod: "sms"
            })
        );

        try {
            const isUserAuthenticated = await dispatch(
                getIsUserAuthenticatedApi.endpoints.getIsUserAuthenticated.initiate(undefined, {forceRefetch: true})
            ).unwrap();

            if (!isUserAuthenticated) {
                throw new Error("SMS login did not create an authenticated session");
            }

            const profile = await dispatch(getUserProfileApi.endpoints.getUserProfile.initiate(undefined, {forceRefetch: true})).unwrap();
            if (!profile) {
                throw new Error("SMS login did not return a user profile");
            }

            await syncDraftToUserProfile(draft, profile);
            await finalizeLeadAfterAuthentication(draft);
        } catch (error) {
            notifyBugsnag(error, "Lead SMS login post-authentication failed", {
                stage: "sms_login_post_auth",
                vendorId: draft.vendorId,
                offerId: draft.offerId,
                propertyId: draft.propertyId
            });
            dispatch(addNotification({notification: defaultAPIErrorNotification}));
            sendSubmitFailedEvent();
        }
    };

    const handlePhoneVerificationSuccess = async () => {
        const draft = mainLead.promotionLeadDraft;

        if (!draft) {
            handleModalClose();
            return;
        }

        await finalizeLeadAfterAuthentication(draft);
    };

    const handlePhoneVerificationChangeNumber = () => {
        sendModalEvent(getEditPhoneEvent(), {
            smsLoginErrorMessage: null,
            smsLoginInitialSendResult: null
        });
    };

    useEffect(() => {
        if (!mainLead.continuePromotionFlowAfterAuth) {
            authFlowWasOpenRef.current = false;
            return;
        }

        if (registrationFlow.isActive) {
            authFlowWasOpenRef.current = true;
            return;
        }

        if (!authFlowWasOpenRef.current || modalState.step !== ApplicationModalStep.PROMOTION_AUTH_FALLBACK || registrationFlow.lastAuthAction) {
            return;
        }

        authFlowWasOpenRef.current = false;
        dispatch(setContinuePromotionFlowAfterAuth(false));
        handleModalClose();
    }, [dispatch, handleModalClose, mainLead.continuePromotionFlowAfterAuth, modalState.step, registrationFlow.isActive, registrationFlow.lastAuthAction]);

    useEffect(() => {
        if (!mainLead.continuePromotionFlowAfterAuth || !isAuthenticated || !userProfile) {
            return;
        }

        if (modalState.step === ApplicationModalStep.PROMOTION_AUTH_FALLBACK && !registrationFlow.lastAuthAction) {
            return;
        }

        authFlowWasOpenRef.current = false;
        if (registrationFlow.lastAuthAction) {
            authActionDuringPromotionFlowRef.current = registrationFlow.lastAuthAction;
        }
        dispatch(setContinuePromotionFlowAfterAuth(false));
        if (registrationFlow.isActive) {
            dispatch(closeFlow());
        }
        void continueLeadAfterAuthentication(userProfile);
    }, [
        dispatch,
        isAuthenticated,
        mainLead.continuePromotionFlowAfterAuth,
        modalState.step,
        registrationFlow.isActive,
        registrationFlow.lastAuthAction,
        userProfile
    ]);

    return {
        handleDraftSubmit,
        handlePhoneVerificationSuccess,
        handlePhoneVerificationChangeNumber,
        handleSmsLoginRejected,
        handleSmsLoginSuccess
    };
};
